/**
 * Custom hook to validate and set Regtank onboarding URL
 * Eliminates duplication of Regtank validation logic
 */

import { useCallback } from "react";
import {
  checkOnboardingLinkValid,
  sortRegtankRequestsByExpiry,
  toISOString,
} from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";
import { RegtankRequest } from "@funded-here-interface/common/src/Utils/navigation/types";
import { UserDetail } from "./useUserDataSync";

interface UseRegtankValidationReturn {
  validateAndSetRegtankUrl: (
    userDetail: UserDetail,
    setRegtankUrl: (url: string) => void
  ) => {
    isValid: boolean;
    detail: RegtankRequest[];
  };
}

/**
 * Hook to validate Regtank onboarding link and set URL if valid
 * @returns Function to validate and set Regtank URL
 */
export const useRegtankValidation = (): UseRegtankValidationReturn => {
  const validateAndSetRegtankUrl = useCallback(
    (userDetail: UserDetail, setRegtankUrl: (url: string) => void) => {
      const requests = userDetail?.regtankRequest;

      // Early return if no requests
      if (!requests || requests.length === 0) {
        return { isValid: false, detail: [] };
      }

      // Sort and get the most recent request
      const sortedRequests = sortRegtankRequestsByExpiry(
        requests
      ) as RegtankRequest[];
      const latestRequest = sortedRequests[0];

      // Check if the link is valid
      const isValid = checkOnboardingLinkValid(
        latestRequest.onboardingStatus,
        toISOString(latestRequest.expiredDate)
      );

      // Set URL if valid
      if (isValid) {
        setRegtankUrl(latestRequest.verifyLink);
      }

      return {
        isValid,
        detail: sortedRequests,
      };
    },
    []
  );

  return { validateAndSetRegtankUrl };
};
